import * as React from 'react'
import { useIsMounted } from 'common'
import { Formik } from 'formik'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { toast } from 'react-toastify'
import FormPersonalData, { PersonalDataType } from 'shared_components/FormPersonalData'
import { ValidationSchemaPersonalDataUpdate } from 'shared_components/FormPersonalData/ValidationSchemaPersonalData'
import { useTypedStoreActions } from 'store/hooks'
import { renderDateFromString } from 'utils/date'
import * as Yup from 'yup'
import { FormMode } from '../../MemberDataTypes'
import PersonalDataCard from './PersonalDataCard'

import styles from 'shared_components/forms.module.scss'
import { SportDataType } from 'shared_components/FormSportData'

interface Props {
    initialValues: PersonalDataType & SportDataType
    mode: FormMode
    changeFormMode: () => void
}

const PersonalData = ({ initialValues, mode, changeFormMode }: Props): JSX.Element => {
    const isMounted = useIsMounted()
    const validationSchema = Yup.object().shape(ValidationSchemaPersonalDataUpdate)
    const updatePersonalData = useTypedStoreActions((state) => state.personalData.updatePersonalData)
    const updateMemberLoginData = useTypedStoreActions((state) => state.authentication.updateMemberLoginData)

    const onCreate = async (values: PersonalDataType) => {
        try {
            const result = await updatePersonalData(values)
            await updateMemberLoginData(result)
            changeFormMode()
        } catch (err) {
            toast.error(<div data-testid="failureToast">{(err as Error).message}</div>)
        }
    }

    if (mode === 'edit') {
        return (
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting }): Promise<void> => {
                    setSubmitting(true)

                    await onCreate(values)

                    if (isMounted()) {
                        setSubmitting(false)
                    }
                }}
            >
                {({ values, handleSubmit, handleReset, isSubmitting }): JSX.Element => (
                    <Form onSubmit={handleSubmit} onReset={handleReset} data-testid="form" className={styles.form}>
                        <FormPersonalData values={values} mode="update" />
                        <Button type="submit" disabled={isSubmitting} className="rounded-0 float-right">
                            Speichern
                        </Button>
                    </Form>
                )}
            </Formik>
        )
    }

    const renderGenger = (gender: string): string => {
        switch (gender) {
            case 'weiblich':
                return 'weiblich'
            case 'männlich':
                return 'männlich'
            case 'unbekannt':
                return 'divers'
            default:
                return 'divers'
        }
    }

    return (
        <div>
            <Row className="pb-4">
                <Col lg={4}>
                    <Row>
                        <Col>
                            <PersonalDataCard cardURL={initialValues.personalData.cardURL} />
                        </Col>
                    </Row>
                </Col>
                <Col lg={4}>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            Titel
                        </Col>
                        <Col>
                            <strong>{initialValues.personalData.title}</strong>
                        </Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            Vorname
                        </Col>
                        <Col>{initialValues.personalData.firstName}</Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            Nachname
                        </Col>
                        <Col>{initialValues.personalData.lastName}</Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            Straße / HausNr.
                        </Col>
                        <Col>
                            {initialValues.personalData.street} {initialValues.personalData.streetNumber}
                        </Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            PLZ
                        </Col>
                        <Col>{initialValues.personalData.zip}</Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            Ort
                        </Col>
                        <Col>{initialValues.personalData.city}</Col>
                    </Row>
                </Col>
                <Col lg={4}>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            Geburtsdatum
                        </Col>
                        <Col>{renderDateFromString(initialValues.personalData.dateOfBirth)}</Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            Geschlecht
                        </Col>
                        <Col>{renderGenger(initialValues.personalData.gender)}</Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            Telefon
                        </Col>
                        <Col>{initialValues.personalData.phone}</Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            E-Mail-Adresse
                        </Col>
                        <Col>{initialValues.personalData.email}</Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={6} className={styles.label}>
                            Erziehungs&shy;berechtigte Person
                        </Col>
                        <Col>{initialValues.personalData.co}</Col>
                    </Row>
                </Col>
            </Row>
        </div>
    )
}

export default PersonalData
